import { useEffect } from 'react';
import { Form, Input, Checkbox, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { SyncIconButton } from './SyncIconButton';
import { DiffCaption, DiffBanner } from './DiffWarning';
import { FloatingField } from './FloatingField';
import {
  getBuyerCccdRules,
  CCCD_PATTERN,
  invoiceEmailRules,
  getInvoiceTaxIdRules,
  requiredRule,
  TAX_CODE_PATTERN,
  isMaskedCccd,
} from './customerFormSchema';
import type { UserType } from '@/pages/POS/types';
import { EMAIL_LIST_HINT } from '@/utils/emailList';

interface Props {
  form: import('antd').FormInstance;
  userType: UserType;
  /** POS customer dialog only. Enables the "Giống thông tin khách hàng" checkbox for BOTH user
   *  types and org copy from customer_tax_code. Absent → legacy behavior (Orders/edit inline). */
  customerMode?: boolean;
  /** Chỉ POS Customer Dialog được bỏ required invoice của khách cá nhân. */
  personalInvoiceOptional?: boolean;
  cccdMasked?: boolean;
  onRevealCccd?: () => void;
  /**
   * TCR-1952 — ghi đè trạng thái khoá riêng cho ô "Email nhận hoá đơn".
   *
   * `undefined` = theo ngữ cảnh bao ngoài (hành vi cũ). `false` = mở ô này DÙ khối bao ngoài đang
   * bị khoá bằng `ConfigProvider componentDisabled` — đúng trường hợp NVKD chỉ có
   * `customers.invoice_email.update`. Prop `disabled` của antd luôn thắng DisabledContext.
   */
  invoiceEmailDisabled?: boolean;
}

function joinDeliveryAddress(form: import('antd').FormInstance) {
  const address = form.getFieldValue('customer_address');
  const wardName = form.getFieldValue('_wardName');
  const cityName = form.getFieldValue('_cityName');
  return [address, wardName, cityName].filter(Boolean).join(', ');
}

export function InvoiceSection({
  form,
  userType,
  customerMode = false,
  personalInvoiceOptional = false,
  cccdMasked,
  onRevealCccd,
  invoiceEmailDisabled,
}: Props) {
  // Set a target field and validate it so any rule violation (e.g. invalid
  // email format inherited from source) surfaces on the target immediately.
  // Without this, sync icons silently propagate bad data.
  const setAndValidate = (targetName: string, value: unknown) => {
    form.setFieldValue(targetName, value);
    form.validateFields([targetName]).catch(() => undefined);
  };

  const buyerCccdVal = Form.useWatch('buyer_cccd', form);
  const isBuyerCccdMasked = cccdMasked || isMaskedCccd(buyerCccdVal);

  const sameAsDelivery = Form.useWatch('invoice_same_as_delivery', form);
  const customerName = Form.useWatch('customer_name', form);
  const customerEmail = Form.useWatch('customer_email', form);
  const citizenId = Form.useWatch('citizen_id', form);
  const customerTaxCode = Form.useWatch('customer_tax_code', form);
  const customerAddress = Form.useWatch('customer_address', form);
  const wardName = Form.useWatch('_wardName', form);
  const cityName = Form.useWatch('_cityName', form);

  const invoiceName = Form.useWatch('invoice_name', form);
  const invoiceTaxId = Form.useWatch('invoice_tax_id', form);
  const invoiceAddress = Form.useWatch('invoice_address', form);
  const invoiceEmail = Form.useWatch('invoice_email', form);

  const deliveryFullAddress = [customerAddress, wardName, cityName].filter(Boolean).join(', ');

  // Organization MST luôn đồng bộ vào hoá đơn — KHÔNG phụ thuộc checkbox
  // "Giống thông tin khách hàng". Đây là số pháp lý gắn với công ty, không có
  // trường hợp hợp lệ nào để nó khác nhau giữa thông tin khách hàng và hoá đơn
  // (TCR-1146: trước đây gate theo checkbox khiến sửa MST bị mất nếu cashier
  // không tick checkbox).
  useEffect(() => {
    if (userType !== 'organization' || !customerMode) return;
    // customerTaxCode === undefined nghĩa là customer_tax_code CHƯA được set (vd:
    // đơn copy từ Orders qua mapOrderToPosPrefill, hoặc tab POS rehydrate từ
    // sessionStorage trước khi fix này được deploy) — KHÔNG phải cashier xoá trắng.
    // Input antd bị xoá tay trả về '' chứ không phải undefined, nên '' vẫn phải
    // được ghi đè bình thường; chỉ undefined mới bỏ qua để tránh xoá nhầm
    // invoice_tax_id đã được prefill sẵn (TCR-1146).
    if (customerTaxCode === undefined) return;
    form.setFieldValue('invoice_tax_id', customerTaxCode);
    form.validateFields(['invoice_tax_id']).catch(() => undefined);
  }, [userType, customerMode, customerTaxCode, form]);

  useEffect(() => {
    if (!sameAsDelivery) return;
    if (userType === 'personal') {
      const COPIED_FIELDS = ['invoice_name', 'invoice_tax_id', 'invoice_address', 'invoice_email', 'buyer_name', 'buyer_cccd'];
      form.setFieldsValue({
        invoice_name: customerName ?? '',
        invoice_tax_id: citizenId ?? '',
        invoice_address: deliveryFullAddress,
        invoice_email: customerEmail ?? '',
        buyer_name: customerName ?? '',
        buyer_cccd: citizenId ?? '',
      });
      // Validate ngay sau khi copy để error/required hiển thị tức thì.
      // validateFields trả Promise reject khi có lỗi — antd tự render lỗi vào
      // Form.Item, ta chỉ cần swallow để tránh unhandled rejection warning.
      form.validateFields(COPIED_FIELDS).catch(() => undefined);
    } else if (customerMode) {
      // organization (POS customer dialog only): copy company name/address.
      // Tax code is synced unconditionally by the effect above (TCR-1146).
      form.setFieldsValue({
        invoice_name: customerName ?? '',
        invoice_address: deliveryFullAddress,
      });
      form.validateFields(['invoice_name']).catch(() => undefined);
    }
  }, [sameAsDelivery, customerName, citizenId, deliveryFullAddress, customerEmail, userType, customerMode, form]);

  const isPersonal = userType === 'personal';
  const orgTaxIdReadOnly = !isPersonal && customerMode;
  const useOptionalPersonalInvoiceRules =
    customerMode && isPersonal && personalInvoiceOptional;
  // Diff captions/banner only make sense when the user has claimed "same as
  // delivery". Otherwise the screen warns about a difference the user never
  // promised wouldn't exist.
  const gateDiff = isPersonal && !!sameAsDelivery;

  const showDiffName =
    gateDiff && invoiceName && customerName && invoiceName !== customerName;
  // Only show "Khác với CCCD khách hàng" once the value is a valid CCCD/MST —
  // otherwise it stacks on top of the format error and becomes unreadable.
  const isInvoiceTaxIdValid =
    !!invoiceTaxId && (CCCD_PATTERN.test(invoiceTaxId) || TAX_CODE_PATTERN.test(invoiceTaxId));
  const showDiffTaxId =
    gateDiff && isInvoiceTaxIdValid && citizenId && invoiceTaxId !== citizenId;
  const showDiffAddress =
    gateDiff &&
    invoiceAddress &&
    deliveryFullAddress &&
    invoiceAddress !== deliveryFullAddress;
  const showDiffEmail =
    gateDiff && invoiceEmail && customerEmail && invoiceEmail !== customerEmail;

  const showBanner =
    showDiffName || showDiffTaxId || showDiffAddress || showDiffEmail;

  return (
    <>
      {(isPersonal || customerMode) && (
        <Form.Item name="invoice_same_as_delivery" valuePropName="checked" style={{ marginBottom: 8 }}>
          <Checkbox>{customerMode ? 'Giống thông tin khách hàng' : 'Giống thông tin giao hàng'}</Checkbox>
        </Form.Item>
      )}

      <FloatingField
        label={isPersonal ? 'Tên khách hàng' : 'Tên công ty xuất hoá đơn'}
        name="invoice_name"
        rules={useOptionalPersonalInvoiceRules ? [] : [requiredRule('tên trên hoá đơn')]}
        required={!useOptionalPersonalInvoiceRules}
      >
        <Input
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ họ tên khách hàng"
              onClick={() =>
                setAndValidate('invoice_name', form.getFieldValue('customer_name') ?? '')
              }
            />
          }
        />
      </FloatingField>
      <DiffCaption show={!!showDiffName} message="Khác với họ tên khách hàng" />

      <FloatingField
        label={isPersonal ? 'CCCD/MST' : 'Mã số thuế'}
        name="invoice_tax_id"
        rules={getInvoiceTaxIdRules(!useOptionalPersonalInvoiceRules)}
        required={!useOptionalPersonalInvoiceRules}
      >
        <Input
          maxLength={14}
          readOnly={orgTaxIdReadOnly}
          title={
            orgTaxIdReadOnly
              ? 'Tự động lấy theo Mã số thuế ở phần Thông tin khách hàng'
              : undefined
          }
          suffix={
            isPersonal ? (
              <SyncIconButton
                tooltip="Đồng bộ từ CCCD khách hàng"
                onClick={() =>
                  setAndValidate('invoice_tax_id', form.getFieldValue('citizen_id') ?? '')
                }
              />
            ) : null
          }
        />
      </FloatingField>
      <DiffCaption show={!!showDiffTaxId} message="Khác với CCCD khách hàng" />

      <FloatingField label="Email nhận hoá đơn" name="invoice_email" rules={invoiceEmailRules}>
        <Input
          // TCR-2432 — ô này nhận danh sách email; gợi ý bằng placeholder + title
          // (không dùng `extra` vì FloatingField đang bố cục label nổi).
          placeholder="email1@company.com, email2@company.com"
          title={EMAIL_LIST_HINT}
          disabled={invoiceEmailDisabled}
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ email khách hàng"
              onClick={() =>
                setAndValidate('invoice_email', form.getFieldValue('customer_email') ?? '')
              }
            />
          }
        />
      </FloatingField>
      <DiffCaption show={!!showDiffEmail} message="Khác với email khách hàng" />

      <FloatingField label="Tên người mua" name="buyer_name">
        <Input
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ họ tên khách hàng"
              onClick={() =>
                setAndValidate('buyer_name', form.getFieldValue('customer_name') ?? '')
              }
            />
          }
        />
      </FloatingField>

      <FloatingField
        label="CCCD người mua"
        name="buyer_cccd"
        rules={getBuyerCccdRules()}
      >
        <Input
          maxLength={12}
          suffix={
            isBuyerCccdMasked && onRevealCccd ? (
              <Tooltip title="Nhấp để giải mã & sửa CCCD">
                <EyeOutlined
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                  onClick={onRevealCccd}
                />
              </Tooltip>
            ) : (
              <SyncIconButton
                tooltip="Đồng bộ từ CCCD khách hàng"
                onClick={() =>
                  setAndValidate('buyer_cccd', form.getFieldValue('citizen_id') ?? '')
                }
              />
            )
          }
        />
      </FloatingField>

      <FloatingField
        label="Địa chỉ xuất hoá đơn"
        name="invoice_address"
        rules={useOptionalPersonalInvoiceRules ? [] : [requiredRule('địa chỉ xuất hoá đơn')]}
        required={!useOptionalPersonalInvoiceRules}
        suffix={
          <SyncIconButton
            tooltip="Đồng bộ từ địa chỉ giao hàng"
            onClick={() => setAndValidate('invoice_address', joinDeliveryAddress(form))}
          />
        }
      >
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
      </FloatingField>
      <DiffCaption show={!!showDiffAddress} message="Khác với địa chỉ giao hàng" />

      <Form.Item name="amis_account_object_id" hidden>
        <Input />
      </Form.Item>

      <DiffBanner show={!!showBanner} />
    </>
  );
}
